import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { useMediaQuery, useTheme } from '@mui/material';
import { FullTabInt } from '../../interfaces';
import { Optional } from '../../types/global';

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
  noTopMargin?: boolean;
  marginTop?: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, noTopMargin, marginTop } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className="tab-content"
      style={noTopMargin ? { marginTop: 0 } : undefined}>
      {value === index && (
        <Box mt={marginTop ?? (noTopMargin ? 0 : 3)}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

interface Props {
  tabs: Optional<FullTabInt, 'tabId'>[];
  currentTab: number;
  changeTab: (tab: number) => void;
  noTopMargin?: boolean;
  marginTop?: number;
  hideWhenOnlyOneTab?: boolean;
  sharedComponent?: React.ReactNode;
}

export default function TabBar({
  tabs,
  currentTab,
  changeTab,
  noTopMargin,
  marginTop,
  hideWhenOnlyOneTab,
  sharedComponent,
}: Props) {
  const theme = useTheme();
  const isMdOrLarger = useMediaQuery(theme.breakpoints.up('md'));
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    changeTab(newValue);
  };

  const filteredTabs = tabs?.filter(tab => !tab.hidden);

  const hideTabBar = hideWhenOnlyOneTab && filteredTabs.length === 1;

  return (
    <Box sx={{ width: '100%' }}>
      {!hideTabBar && (
        <Box sx={{ borderBottom: 1, borderColor: '#EDEEEF' }}>
          <Tabs
            variant={isMdOrLarger ? 'standard' : 'scrollable'}
            scrollButtons={false}
            value={currentTab}
            onChange={handleChange}
            aria-label="tab bar">
            {filteredTabs.map(({ label, id }, i) => (
              <Tab
                key={id || label}
                label={label}
                id={`simple-tab-${i}`}
                style={{
                  fontFamily: theme.typography.fontFamily,
                }}
                aria-controls={`simple-tabpanel-${i}`}
              />
            ))}
          </Tabs>
        </Box>
      )}
      {filteredTabs.map((tab, i) => (
        <TabPanel
          value={currentTab}
          index={tab.tabId ?? i}
          key={tab.label}
          noTopMargin={noTopMargin || hideTabBar}
          marginTop={hideTabBar ? undefined : marginTop}>
          {sharedComponent || tab.component}
        </TabPanel>
      ))}
    </Box>
  );
}
